import * as Sentry from '@sentry/react';

// `isDev` (import.meta.env.DEV) rather than VITE_ENVIRONMENT: every deployed build is a
// `vite build` where DEV is false, so a missing or mislabelled VITE_ENVIRONMENT can never
// switch deployed reporting off — only `vite dev` is silenced, even with a DSN in .env.local.
export function resolveSentryConfig(input: {
  dsn: string | undefined;
  environment: string | undefined;
  isDev: boolean;
}): { dsn: string; environment: string } | null {
  if (input.isDev || !input.dsn) return null;
  return { dsn: input.dsn, environment: input.environment || 'development' };
}

export function initSentry() {
  const config = resolveSentryConfig({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENVIRONMENT,
    isDev: import.meta.env.DEV,
  });
  if (!config) {
    console.warn('[Sentry] Disabled in local development or no DSN configured — error tracking off');
    return;
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    tracesSampleRate: 0,
    // Buffer-only replay: nothing is uploaded unless an error (or feedback) flushes the buffer.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
    ],
  });
}

export function setSentryUser(userId: string, companyId?: string) {
  Sentry.setUser({ id: userId, ...(companyId && { companyId }) });
}

/**
 * Marks events raised in Platform Admin View, where there is no active company and a single
 * session can span every tenant. `null` removes the tag — an ordinary single-company session
 * carries no marker, and the tag must not linger after an admin switches into a company.
 */
export function setSentryViewMode(viewMode: 'platform_admin' | null) {
  Sentry.setTag('view_mode', viewMode ?? undefined);
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

// FIN-865: tags the current Sentry scope with the correlation/session id of the request that
// was just made, so an error captured shortly after (handleError.ts) is findable by that id —
// AC7's "trace: session id, current correlation id" and AC11's cross-system tracing story.
// Sentry tags are scope-global (not per-request), so this is deliberately "tag the most recent
// request" rather than a per-span attribute — good enough for a synchronous browser session
// where requests are rarely concurrent with an error capture racing them.
export function setSentryCorrelationTags(correlationId: string, sessionId: string) {
  Sentry.setTag('correlation_id', correlationId);
  Sentry.setTag('session_id', sessionId);
}

export { Sentry };
